import type { MeResponse } from '@amarantos/api-client'
import { getGetMeQueryOptions, Role } from '@amarantos/api-client'
import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

/**
 * Roles allowed to see and visit the audit log — the app's first role-gate (ADR-0002).
 * Modelled as a set so access can widen without rewriting the checks. Imported by both the
 * nav item (visibility) and the route's `beforeLoad` (access) so they share one source of truth.
 */
export const auditLogRequiredRoles = [Role.owner] as const

export const usersRequiredRoles = [Role.owner] as const

/** Reads the current account's role. `me.person` is present on every `me` shape, so this is safe on the union. */
export function getPanelRole(me: MeResponse): Role {
	return me.person.role
}

/**
 * Whether `role` is allowed by `requiredRoles`. An item with no `requiredRoles`
 * is unrestricted (visible to every authenticated user). Allowed roles are a set
 * so a gate can widen without touching the check itself.
 */
export function hasRequiredRole(role: Role, requiredRoles?: readonly Role[]): boolean {
	return requiredRoles === undefined || requiredRoles.includes(role)
}

export async function requirePanelRole(
	queryClient: Pick<QueryClient, 'ensureQueryData'>,
	requiredRoles: readonly Role[],
): Promise<void> {
	const me = await queryClient.ensureQueryData(getGetMeQueryOptions())

	if (!hasRequiredRole(getPanelRole(me), requiredRoles)) {
		throw redirect({ to: '/' })
	}
}
